import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Typography,
  Button,
  Box,
  InputLabel,
  MenuItem,
  Select,
  FilledInput,
  FormControl
} from "@mui/material";
import Modal from "../Modal/Modal";
import { TextField } from "formik-mui";
import { Field } from "formik";
import { useTranslation } from "react-i18next";
import HTMLEditor from "../HtmlEditor/HtmlEditor";
import { filesPreloading } from "../../services/fileUploadService";

const CreatePageModalForm = ({
  submitForm,
  values,
  onClose,
  open,
  isSubmitting,
  shortCourseTitle,
  resetForm,
  setFieldValue,
  templateOptions,
  filesList,
  setFilterList,
  newVersion
}) => {
  const { t } = useTranslation();
  const [currentTemplate, setCurrentTemplate] = useState("");

  useEffect(() => {
    resetForm();
    setCurrentTemplate("");
  }, [open]);

  const handleTemplateChange = newTemplate => {
    setCurrentTemplate(newTemplate);
    setFieldValue("content", newTemplate.content);
  };

  const getModalTitle = () => {
    return newVersion
      ? t("courseBuilder.createNewVersionPageModalTitle", {
          pageTitle: values.title,
          shortCourseTitle
        })
      : t("courseBuilder.createNewPageModalTitle", { shortCourseTitle });
  };

  return (
    <Form>
      <Modal
        disableEnforceFocus
        showCloseIcon
        maxWidth="lg"
        fullWidth
        dialogTitle={
          <Typography variant="h4" color="textPrimary">
            {getModalTitle()}
          </Typography>
        }
        dialogContent={
          <Box display="flex" flexDirection="column" mb={1}>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              mb={1.5}
              mt={1}
            >
              <Box
                flex={{ xs: 1, md: 2 }}
                mr={{ xs: 0, sm: 3 }}
                mb={{ xs: 3, sm: 0 }}
              >
                <Field
                  variant="filled"
                  fullWidth
                  label={t("courseBuilder.createNewPageTitleLabel")}
                  component={TextField}
                  name="title"
                  inputProps={{
                    "data-qa": "create-page-modal-title-field"
                  }}
                  FormHelperTextProps={{
                    "data-qa": "create-page-modal-title-field-error"
                  }}
                />
              </Box>
              <Box flex={1}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel htmlFor="create-page-modal-template-label">
                    {t("courseBuilder.createNewPageModalTemplateDropdown")}
                  </InputLabel>
                  <Select
                    input={
                      <FilledInput
                        id="create-page-modal-template-label"
                        value={currentTemplate || ""}
                        inputProps={{
                          "data-qa": "create-page-modal-template-select"
                        }}
                      />
                    }
                    onChange={ev => {
                      handleTemplateChange(ev.target.value);
                    }}
                    disableUnderline
                    renderValue={value => value.title}
                  >
                    {templateOptions &&
                      templateOptions.map(option => (
                        <MenuItem key={option.id} value={option}>
                          {t(option.title)}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <HTMLEditor
              disabled={isSubmitting}
              height={300}
              onChange={value => {
                setFieldValue("content", value);
              }}
              value={values.content}
              onFileAdd={(fileObj, tempLink, type) => {
                let list = filesList;
                list.push({ fileObj, tempLink, type });
                setFilterList(list);
              }}
              menubar={false}
              plugins={[
                "lists",
                "link",
                "image",
                "media",
                "code",
                "advlist",
                "fileUpload",
                "textcolor",
                "fullpage"
              ]}
              toolbar="link image media code | styleselect bold italic underline | outdent indent bullist | fontselect fontsizeselect forecolor"
            />
          </Box>
        }
        dialogActions={
          <>
            <Button color="primary" disabled={isSubmitting} onClick={onClose}>
              {t("courseBuilder.createNewPageCancelBtn")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={submitForm}
              disabled={isSubmitting}
              data-qa="submit-btn"
            >
              {t("courseBuilder.createNewPageSaveBtn")}
            </Button>
          </>
        }
        data-qa="create-page-modal"
        open={open}
        onClose={onClose}
      />
    </Form>
  );
};
const getCreatePageSchema = t => {
  return Yup.object().shape({
    title: Yup.string().required(t("validationMessages.titleRequired"))
  });
};

const CreatePageModal = ({
  onClose,
  open,
  onSubmit,
  shortCourseTitle,
  shortCoursePageTemplates,
  shortCourseId,
  coursePage
}) => {
  const { t } = useTranslation();
  const [filesList, setFilterList] = useState([]);
  return (
    <Formik
      initialValues={{
        title: coursePage ? coursePage.name : "",
        content: coursePage ? coursePage.content : "",
        course_builder_page: true
      }}
      validationSchema={getCreatePageSchema(t)}
      enableReinitialize
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        const tmpObj = await filesPreloading(filesList, values.content);
        values.images = tmpObj.images;
        values.media = tmpObj.media;
        values.attachments = tmpObj.attachments;
        values.content = tmpObj.content;
        setSubmitting(true);
        onSubmit(shortCourseId, values, () => {
          setSubmitting(false);
        });
        resetForm();
        onClose();
      }}
    >
      {({ submitForm, isSubmitting, values, resetForm, setFieldValue }) => (
        <CreatePageModalForm
          submitForm={submitForm}
          isSubmitting={isSubmitting}
          values={values}
          resetForm={resetForm}
          open={open}
          onClose={onClose}
          setFieldValue={setFieldValue}
          shortCourseTitle={shortCourseTitle}
          templateOptions={shortCoursePageTemplates}
          filesList={filesList}
          setFilterList={setFilterList}
          newVersion={coursePage ? true : false}
        />
      )}
    </Formik>
  );
};

CreatePageModal.propTypes = {
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to create the new page
   */
  onSubmit: PropTypes.func,
  /**
   * The title of the short course that the new page will be added to
   */
  shortCourseTitle: PropTypes.string,
  shortCourseId: PropTypes.string
};

export default CreatePageModal;
